import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import { DataGrid, DataGridPreview } from '@afosto/app-components-react/DataGrid';
import Portal from '@material-ui/core/Portal';
import UserForm from '../../../components/UserForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import { userFormatter } from '../formatters';
import { rolesFormatter } from './formatters';
import { users as usersApi } from '@afosto/iam/api/Users';
import { rbac as rbacApi } from '@afosto/iam/api/Rbac';
import translations from './translations';

const fetchUsers = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => usersApi.listUsers({ isPendingInvite: 0 }, requestOptions),
    {
      transformData: async data => {
        try {
          const rolesResponse = await Promise.all([rbacApi.listRoles(), rbacApi.listSystemRoles()]);
          const availableRoles = rolesResponse.reduce(
            (acc, { data: rolesData }) => [...acc, ...rolesData],
            [],
          );

          return data.reduce((acc, user) => {
            const roles = user.roles.reduce((acc2, role) => {
              const userRole = availableRoles.find(availableRole => availableRole.id === role);
              return [...acc2, userRole?.name ?? role];
            }, []);

            return [
              ...acc,
              {
                ...user,
                roles,
              },
            ];
          }, []);
        } catch (err) {
          return [];
        }
      },
    },
  );

const UsersOverview = ({ container }) => {
  usePrivate();

  const intl = useIntl();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState(null);
  const [isFetchingUser, setIsFetchingUser] = useState(null);
  const [showEditUser, setShowEditUser] = useState(false);

  const dataSource = useDataGrid(fetchUsers, {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'user',
          name: intl.formatMessage({
            id: 'dataGrid.columns.users.user',
            defaultMessage: 'User',
            description: 'The column name for column user',
          }),
          type: 'user',
        },
        {
          key: 'roles',
          name: intl.formatMessage({
            id: 'dataGrid.columns.users.roles',
            defaultMessage: 'Roles',
            description: 'The column name for column roles',
          }),
          type: 'roles',
        },
        {
          key: 'is_verified',
          name: intl.formatMessage({
            id: 'dataGrid.columns.users.is_verified',
            defaultMessage: 'Is verified',
            description: 'The column name for column is_verified',
          }),
          format: null,
        },
      ],
    },
  });
  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem, setActiveItem } = activeItemModule;

  const handleDeleteUser = async () => {
    try {
      const answer = await confirm({
        title: intl.formatMessage(translations.confirmDeleteUser.title),
        description: intl.formatMessage(translations.confirmDeleteUser.description),
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDeleteUser.actions.cancel),
            response: 'cancel',
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmDeleteUser.actions.delete),
            response: 'delete',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
        catchOnCancel: false,
      });

      if (answer === 'delete') {
        await usersApi.deleteUser(user.id);
        enqueueSnackbar(intl.formatMessage(translations.feedback.userDeleted), {
          variant: 'success',
        });
        setUser(null);
        setActiveItem(null);
        dataSource.reloadDataSource();
      }
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.userNotDeleted), {
        variant: 'error',
      });
    }
  };

  /**
   * Fetch the user data if a user row is selected and show the data in the preview.
   */
  useEffect(() => {
    let isMounted = true;

    if (activeItem) {
      /**
       * Fetch a users data by its id
       * @param {String} id
       * @returns {Promise}
       */
      const fetchUser = async id => {
        setIsFetchingUser(true);
        try {
          const response = await usersApi.getUser(id);
          const { data = {} } = response;

          if (isMounted) {
            setUser(data);
          }
        } catch (err) {
          if (isMounted) {
            setUser(null);
          }
        } finally {
          if (isMounted) {
            setIsFetchingUser(false);
          }
        }
      };

      fetchUser(activeItem);
    } else {
      setUser(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  /**
   * Handle the onSubmit event of the user form in the grid preview.
   * @param {Array} roles
   * @param {Object} userData
   * @returns {Promise}
   */
  const handleSaveUser = async ({ roles, firstName, lastName }) => {
    try {
      const userResponse = await usersApi.updateUser(user.id, {
        first_name: firstName,
        last_name: lastName,
      });
      const rolesResponse = await usersApi.updateUserRoles(
        user.id,
        roles.map(role => role.value),
      );

      setUser({ ...userResponse.data, ...rolesResponse.data });
      dataSource.reloadDataSource();

      setShowEditUser(false);

      enqueueSnackbar(intl.formatMessage(translations.feedback.userUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.userNotUpdated), {
        variant: 'error',
      });
    }
  };

  const previewMenu = [
    ...(user
      ? [
          {
            key: 'user_edit',
            translationKey: 'actions.edit',
            label: 'Edit',
            icon: 'pencil',
            shortcut: true,
            onClick: () => setShowEditUser(true),
          },
          {
            key: 'user_delete',
            translationKey: 'actions.delete',
            label: 'Delete',
            icon: 'trash-alt',
            color: 'secondary',
            onClick: handleDeleteUser,
          },
        ]
      : []),
  ];

  return (
    <>
      <DataGrid
        dataSource={dataSource}
        formatters={{ roles: rolesFormatter, user: userFormatter }}
      />
      <Portal container={container.current}>
        <DataGridPreview
          menu={previewMenu}
          open={dataSource.activeItem.activeItem !== null}
          onClose={() => dataSource.activeItem.setActiveItem(null)}
          title={user ? `${user.first_name} ${user.last_name}` : null}
        >
          <LoadingWrapper isLoading={isFetchingUser}>
            {user && <UserForm inline user={user} onSubmit={handleSaveUser} isSubmitted />}
          </LoadingWrapper>
        </DataGridPreview>
        <FormDrawer
          title={intl.formatMessage(translations.edit.title)}
          isOpen={showEditUser}
          onClose={() => setShowEditUser(false)}
          drawerWidth="400px"
        >
          <UserForm user={user} onCancel={() => setShowEditUser(false)} onSubmit={handleSaveUser} />
        </FormDrawer>
      </Portal>
    </>
  );
};

UsersOverview.propTypes = {
  container: PropTypes.object,
};

UsersOverview.defaultProps = {
  container: undefined,
};

export default UsersOverview;
